import React from "react";
import * as R from "ramda";
import { css } from "glamor";

import { getJsonSchemaFromPath, removeTypenames } from "../util";
import { formatWithJsonSchema, normalize } from "../formatters";
import GenericViewer from "../component/GenericViewer";

const styles = {
    firstLevelTitle: css({
        backgroundColor: "#e6f6fd",
        fontWeight: 700,
        padding: "5px 10px",
        margin: "20px 10px 10px 0",
        borderRadius: 5,
        display: "flex",
        alignItems: "center",
    }),
    firstLevelContent: css({
        marginLeft: 10,
    }),
};

const getAdditionalProperties = entity =>
    R.propOr({}, ["additionalProperties"], entity);

const getLocalLanguageName = entity => {
    const altNames = entity.alternativeNames;
    return altNames ? { localLanguageName: altNames.localNames[0] } : {};
};

const getNonEditableProps = entity =>
    R.pickBy(
        (val, key) => val && key !== "@type" && key !== "subject",
        getAdditionalProperties(entity)
    );

const getDomiciledAddress = entity => {
    const domiciled = R.omit(
        ["type"],
        R.pathOr([], ["addresses"], entity).find(R.propEq("type", "DOMICILED"))
    );

    return R.isEmpty(domiciled) ? domiciled : { domiciledAddress: domiciled };
};

export const generateEntityData = entity => {
    return {
        name: entity.name,
        ...getLocalLanguageName(entity),
        ...R.pick(
            [
                "externalIdentifiers",
                "linesOfBusiness",
                "entityType",
                "identificationNumber",
                "dateOfBirth",
                "nationality",
                "dateOfIncorporation",
                "registrationStatus",
                "legalAddress",
                "physicalAddress",
            ],
            entity
        ),
        ...getDomiciledAddress(entity),
        ...R.pick(
            [
                "riskLevel",
                "riskScore",
                "isArchived",
                "ownershipType",
                "stockExchangeOfPrimaryListing",
                "totalAssets",
                "totalAnnualRevenue",
                "fiscalYearEndDate",
                "highRiskIndustryDesignation",
                "primaryIndustryCode",
                "primaryIndustryDescription",
                "regulationStatus",
                "natureOfBusiness",
                "exchangeTradedStatus",
            ],
            entity
        ),
        ...getNonEditableProps(entity),
    };
};

const hotfix_for_T9155 = R.omit([
    "frequency",
    "screeningLevel",
    "monitoringLevel",
    "source",
]);

export default class EntityViewer extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            schema: this.props.schema,
            editable: this.props.editable || false,
        };
        this.nameMapper =
            this.props.nameMapper ||
            (p => {
                if (p === "monitoringLevel") return "Screening Level";

                return null;
            });
    }

    renderRows(properties) {
        if (properties === undefined) return;
        return Object.keys(properties).map((property, i) => {
            const field = properties[property];
            if (
                ["@type", "extra"].indexOf(property) !== -1 ||
                !field ||
                (Array.isArray(field) && !field.length) ||
                (typeof field === "object" &&
                    !Object.values(field).filter(field => !!field).length)
            ) {
                return null;
            }

            const childSchema = getJsonSchemaFromPath(
                property,
                this.state.schema
            );
            if (this.props.hideUnknownFields) {
                const value = formatWithJsonSchema({
                    jsonSchema: childSchema,
                    data: field,
                });
                if (/unknown/i.test(value)) {
                    return null;
                }
            }
            return (
                <div key={property || i}>
                    <div className={styles.firstLevelTitle}>
                        {this.nameMapper(property) || normalize(property)}
                    </div>
                    <div className={styles.firstLevelContent}>
                        <GenericViewer
                            ignoreEmpty
                            data={field}
                            schema={childSchema}
                        />
                    </div>
                </div>
            );
        });
    }

    render() {
        const data = [
            removeTypenames(this.props.entity.key),
            generateEntityData(removeTypenames(this.props.entity)),
        ];

        const additionalRows = this.renderRows(
            hotfix_for_T9155(getAdditionalProperties(this.props.entity).extra)
        );

        return (
            <React.Fragment>
                {data.map(row => {
                    return this.renderRows(row);
                })}
                <div>{additionalRows}</div>
            </React.Fragment>
        );
    }
}
